import React, {
  type DragEventHandler,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { formatFileSize } from "senf-shared/utils/helpers";
import { tv } from "tailwind-variants";
import { useIsDragging } from "../../../hooks/useIsDragging";
import { cn } from "../../../util/classNameConstructor";
import { getFileIcon } from "../../special/imageModifier/MediaBrowser";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";
import { bodySm } from "../typography/Typography";

const FileDropVariant = tv({
  base: "flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl p-2",
  slots: {
    label: "relative p-0.5 text-center text-white",
  },
  variants: {
    hidden: {
      false:
        "text-text-subdued [&_.file-drop-icon_svg]:text-icon-subdued bg-surface-subdued hover:border-action-primary-default hover:bg-surface-highlight-subdued hover:text-[var(--primary-600)] hover:text-[var(--primary-600)] [&:hover_.file-drop-icon_svg]:text-[var(--primary-600)] [&:hover_.file-drop-icon_svg]:text-[var(--primary-600)] cursor-pointer",
      true: "!border-transparent",
    },
    drag: {
      true: "border-action-primary-default bg-surface-highlight-subdued border-2",
      false: "border-border-default border-2 border-dashed",
    },
    file: {
      true: "",
    },
  },
});

interface FileDropProps {
  onDrop: (files: FileList | File[]) => void;
  onClick?: () => void;
  text?: string | false;
  className?: string;
  /** For icon-only / compact drops */
  "aria-label"?: string;
  classNames?: {
    image?: string;
    icon?: string;
    text?: string;
    /** Inner area around icon + file input (default padding `p-4`) */
    content?: string;
  };
  icon?: IconName;
  loading?: boolean;
  showImage?: boolean;
  multiple?: boolean;
  fileTypes?: string[];
  fileTypesText?: string | false;
  hidden?: boolean;
  stagedFiles?: File[];
  onRemoveStagedFile?: (index: number) => void;
  showStagedFiles?: boolean;
  showSkeletons?: boolean; // Whether to show skeleton placeholders
  skeletonCount?: number; // Number of skeleton placeholders to show
}

const FileDrop = forwardRef<HTMLInputElement, FileDropProps>(
  (
    {
      onDrop,
      onClick,
      className,
      classNames,
      showImage,
      multiple,
      hidden,
      text,
      icon,
      loading,
      fileTypes = ["image/jpg", "image/png", "image/jpeg"],
      fileTypesText,
      stagedFiles = [],
      onRemoveStagedFile,
      showStagedFiles = true,
      showSkeletons = false,
      skeletonCount = 1,
      "aria-label": ariaLabel,
    },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const [drag, setDrag] = useState(false);
    const [files, setFiles] = useState<FileList | File[]>();
    const { base } = FileDropVariant({
      drag: !!drag,
      file: !!files?.length,
      hidden: !!hidden && !drag,
    });

    const strippedFileTypes = fileTypes
      .filter((type) => type && type !== "image/jpeg")
      .map((type) => {
        return `.${type.replace(".", "").split("/").pop()}`;
      })
      .join(", ");

    const subtext =
      fileTypesText === false ? null : fileTypesText || strippedFileTypes;

    const dropContainer = useRef<HTMLDivElement>(null);
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const inputRef = forwardedRef || defaultInputRef;

    const processDrag = useCallback(
      (e: Event | React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      },
      [],
    );

    const handleDrag = useCallback(
      (e: Event) => {
        e.preventDefault();
        processDrag(e);
      },
      [processDrag],
    );

    const handleDragIn = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        processDrag(e);
        setDrag(true);
      },
      [processDrag],
    );

    const handleDragOut = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        processDrag(e);
        // Only set drag to false if we're leaving the actual drop container
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDrag(false);
        }
      },
      [processDrag],
    );

    const handleDrop = useCallback(
      (
        e: DragEvent | React.ChangeEvent<HTMLInputElement>,
        type?: "inputFile",
      ) => {
        if (type === "inputFile" && !(e instanceof DragEvent)) {
          const { files } = e.target;
          if (files && files.length > 0) {
            setFiles(files);
            onDrop(files);
            // Reset the value of the input to ensure a change is detected next time
            e.target.value = "";
          }
        } else if (e instanceof DragEvent) {
          const { files } = e.dataTransfer || {};
          processDrag(e);
          setDrag(false);
          if (files && files.length > 0) {
            setFiles(multiple ? files : [files[0]]);
            onDrop(multiple ? files : [files[0]]);
          }
        }
      },
      [multiple, onDrop, processDrag],
    );

    const isDragging = useIsDragging();
    React.useEffect(() => {
      const div = dropContainer.current;

      div?.addEventListener("dragover", handleDrag);
      div?.addEventListener("drop", handleDrop);
      return () => {
        div?.removeEventListener("dragover", handleDrag);
        div?.removeEventListener("drop", handleDrop);
      };
    }, [handleDrag, handleDrop]);

    const fileIcon = useMemo<IconName>(() => {
      const icons = fileTypes.map((type) => getFileIcon(type, multiple));
      if (icons.length === new Set(icons).size) {
        return icons[0];
      }
      return getFileIcon(undefined, multiple);
    }, [fileTypes, multiple]);

    return (
      <div
        aria-label={ariaLabel}
        className={cn(
          base({ className }),
          isDragging || drag
            ? "!border-action-primary-default z-50 rounded-lg"
            : hidden
              ? "-z-50"
              : "",
        )}
        ref={dropContainer}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={() => {
          if (onClick) {
            onClick();
          } else if (!hidden) {
            (inputRef as React.RefObject<HTMLInputElement>).current?.click();
          }
        }}
      >
        <div className="flex flex-col h-full">
          {/* Main Drop Area */}
          <div className="flex-1 flex flex-col justify-center">
            {[...(files || [])]?.map((file) =>
              showImage && fileTypes.includes(file.type) ? (
                <img
                  key={file.name}
                  src={URL.createObjectURL(file)}
                  className={cn(classNames?.image)}
                  alt={file.name}
                />
              ) : null,
            )}
            {!showImage ||
            ![...(files || [])].some((file) =>
              fileTypes.includes(file.type),
            ) ? (
              <div
                className={cn(
                  drag ? "pointer-events-none" : "",
                  hidden
                    ? ""
                    : "flex flex-1 flex-col justify-center p-4",
                  classNames?.content,
                )}
              >
                {(!hidden || !!icon) && (
                  <Icon
                    name={loading ? "loading" : icon || fileIcon || "add-image"}
                    className={cn(
                      "file-drop-icon mx-auto mb-2 grayscale mix-blend-luminosity",
                      classNames?.icon,
                    )}
                  />
                )}
                {!hidden && text !== false && (
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span
                      className={cn(
                        "flex flex-col gap-2 text-center",
                        bodySm,
                        classNames?.text,
                      )}
                    >
                      {text ||
                        (fileTypes?.every((e) => e.includes("image"))
                          ? multiple
                            ? t("util:dragAndDrop", { val: t("global:images") })
                            : t("util:dragAndDrop", { val: t("global:image") })
                          : fileTypes?.every((e) => e.includes("video"))
                            ? multiple
                              ? t("util:dragAndDrop", {
                                  val: t("global:videos"),
                                })
                              : t("util:dragAndDrop", {
                                  val: t("global:video"),
                                })
                            : multiple
                              ? t("util:dragAndDrop", {
                                  val: t("global:files"),
                                })
                              : t("util:dragAndDrop", {
                                  val: t("global:file"),
                                }))}
                    </span>
                    {subtext && (
                      <span className="body-sm font-size-[5px] flex gap-1 whitespace-nowrap text-center">
                        ({subtext})
                      </span>
                    )}
                  </div>
                )}
                <input
                  className="hidden opacity-0"
                  type="file"
                  accept={fileTypes.join(",")}
                  ref={inputRef}
                  onChange={(e) => handleDrop(e, "inputFile")}
                  multiple={multiple}
                />
              </div>
            ) : null}
          </div>
        </div>

        {showStagedFiles &&
          (stagedFiles.length > 0 || (showSkeletons && skeletonCount > 0)) && (
            <div className="space-y-2 max-h-96 overflow-y-auto w-full">
              {/* Actual staged files */}
              {stagedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between p-2 bg-white text-sm border rounded-lg w-full"
                >
                  <div className="flex-1 min-w-0">
                    <div className="body-sm text-text-default truncate">
                      {file.name}
                    </div>
                    <div className="text-gray-500">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                  {onRemoveStagedFile && (
                    <Button
                      variant="shapeless"
                      size="sm"
                      leadingIcon="trash"
                      critical
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveStagedFile(index);
                      }}
                    />
                  )}
                </div>
              ))}

              {/* Skeleton placeholders during extraction - only show if no real files yet */}
              {showSkeletons &&
                stagedFiles.length === 0 &&
                Array.from({ length: skeletonCount }, (_, index) => (
                  <div
                    key={`skeleton-loading-${index}-${Date.now()}`}
                    className="flex items-center justify-between p-2 bg-white text-sm border rounded-lg w-full animate-pulse"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
                      <div className="h-3 bg-gray-200 rounded w-1/4" />
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded" />
                  </div>
                ))}
            </div>
          )}
      </div>
    );
  },
);

FileDrop.displayName = "FileDrop";

export default React.memo(FileDrop);
